import { grey } from '@pelotoncycle/design-system'
import { DialogContent, DialogOverlay } from '@reach/dialog'
import '@reach/dialog/styles.css'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Box, Flex } from '.'
import { HamburgerMenu, X } from './svg'

type TimeoutType = ReturnType<typeof setTimeout>

// source: https://github.com/reach/reach-ui/issues/568#issuecomment-622114793
const useDelayUnmount = (showModal: boolean, delayTime: number) => {
  const [shouldRender, setShouldRender] = useState(false)

  useEffect(() => {
    let timeoutId: TimeoutType | undefined

    if (showModal && !shouldRender) {
      setShouldRender(true)
    } else if (!showModal && shouldRender) {
      timeoutId = setTimeout(() => setShouldRender(false), delayTime)
    }

    return () => clearTimeout(timeoutId as TimeoutType)
  }, [showModal, delayTime, shouldRender])

  return shouldRender
}

type MobileMenuVariant = 'default' | 'brand'

const VARIANT_BACKGROUNDS: Record<MobileMenuVariant, string> = {
  default: grey[90],
  brand: 'var(--theme-primary)',
}

type MobileMenuProps = {
  children: (onClose: () => void) => ReactNode
  variant?: MobileMenuVariant
}

const MobileMenu = ({ children, variant = 'default' }: MobileMenuProps) => {
  const [showModal, setShowModal] = useState(false)
  const shouldRenderChild = useDelayUnmount(showModal, 380)
  const { t } = useTranslation()
  const dropIn = { animation: `dropIn 400ms ease 0s` }
  const liftOut = { animation: `liftOut 400ms ease 0s` }
  const ariaLabel: string = t('navigation_menu')
  const onClose = () => setShowModal(false)

  return (
    <Box marginLeft="24px" data-testid="mobile-menu">
      <button
        style={{ height: '100%' }}
        type="button"
        onClick={() => setShowModal(true)}
        aria-label={t('open_menu')}
        tabIndex={shouldRenderChild ? -1 : 0}
      >
        <HamburgerMenu />
      </button>
      {shouldRenderChild && (
        <StyledOverlay>
          <StyledContent
            style={showModal ? dropIn : liftOut}
            aria-label={ariaLabel}
            variant={variant}
          >
            <Flex width="100%" justifyContent="flex-end" marginBottom="24px">
              <button type="button" onClick={onClose} aria-label={t('close_menu')}>
                <X width="20px" height="20px" />
              </button>
            </Flex>
            {children(onClose)}
          </StyledContent>
        </StyledOverlay>
      )}
    </Box>
  )
}

const StyledOverlay = styled(DialogOverlay)`
  background: transparent;
  z-index: 10;
`

// works for fade in but not out
const StyledContent = styled(DialogContent).withConfig({
  shouldForwardProp: prop => prop !== 'variant',
})<{ variant: MobileMenuVariant }>`
  width: 100vw;
  height: 100vh;
  background-color: ${p => VARIANT_BACKGROUNDS[p.variant]};
  margin: 0;

  @keyframes dropIn {
    from {
      transform: translate3d(0px, -100%, 0px);
    }
    to {
      transform: translate3d(0px, 0px, 0px);
    }
  }

  @keyframes liftOut {
    from {
      transform: translate3d(0px, 0px, 0px);
    }
    to {
      transform: translate3d(0px, -103%, 0px);
    }
  }
`

export { MobileMenu }
